import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';
import { Subject } from 'rxjs';

export type RewardCatalogType = 'all' | 'reward' | 'snack';

@Injectable({
  providedIn: 'root',
})
export class RewardsAdminService {
  private rewardsChanged = new Subject<string>();

  constructor(private restangular: Restangular) {}

  monitorRewardsChanged(func: (id: string) => void): void {
    this.rewardsChanged.subscribe((value) => func(value));
  }

  private notifyRewardsChanged(id: string): void {
    this.rewardsChanged.next(id);
  }

  getRewards(filter = '', page = 1, limit = 30, type: RewardCatalogType = 'all'): Promise<any[]> {
    return this.restangular.one('admin').one('reward').one('rewards').customGET('', { filter, page, limit, type }).toPromise();
  }

  getSnackRewardCandidates(brand: string, companyId?: string): Promise<any[]> {
    const params: any = { brand };
    if (companyId) {
      params.companyId = companyId;
    }
    return this.restangular.one('admin').one('reward').one('snack-candidates').customGET('', params).toPromise();
  }

  getGloballySelectableSnackRewardCandidates(): Promise<any[]> {
    return this.restangular
      .one('admin')
      .one('reward')
      .one('snack-candidates')
      .customGET('', { scope: 'global' })
      .toPromise();
  }

  getSnackRewardCandidatesForBrandSource(brand: string): Promise<any[]> {
    return this.restangular
      .one('admin')
      .one('reward')
      .one('snack-candidates')
      .customGET('', { brand, scope: 'brand' })
      .toPromise();
  }

  getPayrollAdminSnackRewardCandidates(companyId: string, brand: string): Promise<any[]> {
    return this.restangular
      .one('payroll')
      .one('companies', companyId)
      .one('snack-candidates')
      .customGET('', { brand, scope: 'company-selection' })
      .toPromise();
  }

  getRewardById(rewardId: string): Promise<any> {
    return this.restangular.one('admin').one('reward').one('rewards', rewardId).get().toPromise();
  }

  getCampaignsForReward(rewardId: string): Promise<any[]> {
    return this.restangular.one('admin').one('reward').one('campaign', rewardId).getList().toPromise();
  }

  putCampaignsForReward(rewardId: string, campaignIds: string[]): Promise<any> {
    return this.restangular.one('admin').one('reward').one('campaign', rewardId).customPUT({ campaigns: campaignIds }).toPromise();
  }

  getBrands(filter = '', page = 0, limit = 10000): Promise<any[]> {
    return this.restangular.one('admin').one('reward').one('brands').customGET('', { filter, page, limit }).toPromise();
  }

  getBrandById(brandId: string): Promise<any> {
    return this.restangular.one('admin').one('reward').one('brands', brandId).get().toPromise().catch(async (error: any) => {
      const status = error?.status || error?.originalError?.status;
      if (status !== 404) {
        throw error;
      }

      const brands = await this.getBrands('', 0, 10000);
      const matchingBrand = (brands || []).find((brand: any) => {
        const id = String(brand?.id || brand?.brandId || brand?._id || '').trim();
        return id === String(brandId || '').trim();
      });

      if (!matchingBrand) {
        throw error;
      }

      return matchingBrand;
    });
  }

  getCampaigns(filter = '', page = 0, limit = 10000): Promise<any[]> {
    return this.restangular.one('admin').one('reward').one('campaigns').customGET('', { nofilter: 1, filter, page, limit }).toPromise();
  }

  getActiveRewards(brand: string): Promise<any[]> {
    return this.restangular.one('v3').one('commissionjunction').one('search').customGET('', { brand }).toPromise();
  }

  getCampaignById(campaignId: string): Promise<any> {
    return this.restangular.one('admin').one('reward').one('campaigns', campaignId).get().toPromise();
  }

  getRewardsForCampaign(campaignId: string): Promise<any[]> {
    return this.restangular.one('admin').one('reward').one('campaigns', campaignId).getList().toPromise();
  }

  getClaimedRewards(filter = '', rewardId = '', page = 1, limit = 30): Promise<any[]> {
    return this.restangular.one('admin').one('reward').one('claimedrewards').customGET('', { filter, page, limit, rewardId }).toPromise();
  }

  getClaimedRewardById(claimedId: string): Promise<any> {
    return this.restangular.one('admin').one('reward').one('claimedrewards', claimedId).get().toPromise();
  }

  postReward(reward: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('rewards').customPOST(reward).toPromise().then((result: any) => {
      this.notifyRewardsChanged(result?.id || '');
      return result;
    });
  }

  changeRewardImage(rewardId: string, image: any): Promise<any> {
    return this.restangular.one('admin').one('reward', rewardId).one('image').customPUT(image).toPromise();
  }

  postBrand(brand: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('brands').customPOST(brand).toPromise().then((result: any) => {
      this.notifyRewardsChanged(result?.id || '');
      return result;
    });
  }

  postCampaign(campaign: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('campaigns').customPOST(campaign).toPromise().then((result: any) => {
      this.notifyRewardsChanged(result?.id || '');
      return result;
    });
  }

  postClaimedReward(claimedReward: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('claimedrewards').customPOST(claimedReward).toPromise().then((result: any) => {
      this.notifyRewardsChanged(result?.id || '');
      return result;
    });
  }

  putReward(reward: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('rewards', reward.id).customPUT(reward).toPromise().then((result: any) => {
      this.notifyRewardsChanged(reward.id);
      return result;
    });
  }

  putBrand(brand: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('brands', brand.id).customPUT(brand).toPromise().then((result: any) => {
      this.notifyRewardsChanged(brand.id);
      return result;
    });
  }

  putCampaign(campaign: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('campaigns', campaign.id).customPUT(campaign).toPromise().then((result: any) => {
      this.notifyRewardsChanged(campaign.id);
      return result;
    });
  }

  putClaimedReward(claimedReward: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('claimedrewards', claimedReward.id).customPUT(claimedReward).toPromise().then((result: any) => {
      this.notifyRewardsChanged(claimedReward.id);
      return result;
    });
  }

  deleteReward(reward: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('rewards', reward.id).customDELETE().toPromise().then((result: any) => {
      this.notifyRewardsChanged(reward.id);
      return result;
    });
  }

  deleteBrand(brand: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('brands', brand.id).customDELETE().toPromise().then((result: any) => {
      this.notifyRewardsChanged(brand.id);
      return result;
    });
  }

  deleteCampaign(campaign: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('campaigns', campaign.id).customDELETE().toPromise().then((result: any) => {
      this.notifyRewardsChanged(campaign.id);
      return result;
    });
  }

  deleteClaimedReward(claimedReward: any): Promise<any> {
    return this.restangular.one('admin').one('reward').one('claimedrewards', claimedReward.id).customDELETE().toPromise().then((result: any) => {
      this.notifyRewardsChanged(claimedReward.id);
      return result;
    });
  }

  getPurchasedRewards(filter = '', page = 1, limit = 30): Promise<any[]> {
    return this.restangular.one('admin').one('rewards').one('purchasedRewards').customGET('', { filter, page, limit }).toPromise();
  }

  getPurchasedRewardById(id: string): Promise<any> {
    return this.restangular.one('admin').one('rewards', id).one('purchasedRewards').get().toPromise();
  }

  postPurchasedReward(reward: any): Promise<any> {
    return this.restangular.one('admin').one('rewards').one('purchasedRewards').customPOST(reward).toPromise();
  }

  putPurchasedRewardById(id: string, reward: any): Promise<any> {
    return this.restangular.one('admin').one('rewards', id).one('purchasedRewards').customPUT(reward).toPromise();
  }

  deletePurchasedRewardById(id: string): Promise<any> {
    return this.restangular.one('admin').one('rewards', id).one('purchasedRewards').customDELETE().toPromise();
  }

  getBrandUsers(): Promise<any[]> {
    return this.restangular.one('admin').one('rewards').one('brandUsers').getList().toPromise();
  }

  postBrandUser(brandId: string, userId: string): Promise<any> {
    return this.restangular.one('admin').one('rewards').one('brandUsers', brandId).one(userId).customPOST({}).toPromise();
  }

  deleteBrandUser(brandId: string, userId: string): Promise<any> {
    return this.restangular.one('admin').one('rewards').one('brandUsers', brandId).one(userId).customDELETE().toPromise();
  }

  importRewards(rewards: any[]): Promise<any> {
    return this.restangular.one('admin').one('rewards').one('import').customPOST({ rewards }).toPromise();
  }
}
